import { useCallback, useEffect, useRef, useState } from 'react'
import { lookupStudentByEmail } from 'pages/UserManagement/model/lookupStudentsApi'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DEBOUNCE_MS = 400

export const useAddStudentSearch = ({ enabled, onLookupError }) => {
	const [nameQuery, setNameQuery] = useState('')
	const [emailQuery, setEmailQuery] = useState('')
	const [lookupResult, setLookupResult] = useState(null)
	const [selectedStudent, setSelectedStudent] = useState(null)
	const [lookupStatus, setLookupStatus] = useState('idle')
	const requestIdRef = useRef(0)

	const reset = useCallback(() => {
		requestIdRef.current += 1
		setNameQuery('')
		setEmailQuery('')
		setLookupResult(null)
		setSelectedStudent(null)
		setLookupStatus('idle')
	}, [])

	useEffect(() => {
		if (!enabled) {
			reset()
		}
	}, [enabled, reset])

	useEffect(() => {
		if (!enabled) return undefined

		const trimmed = emailQuery.trim()
		if (selectedStudent && trimmed.toLowerCase() === String(selectedStudent.email || '').toLowerCase()) {
			return undefined
		}

		setSelectedStudent(null)
		setLookupResult(null)

		if (!trimmed || !EMAIL_PATTERN.test(trimmed)) {
			setLookupStatus('idle')
			return undefined
		}

		setLookupStatus('loading')
		const requestId = ++requestIdRef.current
		const timer = window.setTimeout(async () => {
			try {
				const student = await lookupStudentByEmail(trimmed)
				if (requestId !== requestIdRef.current) return

				if (!student) {
					setLookupStatus('not_found')
					setLookupResult(null)
					return
				}

				setLookupResult(student)
				if (student.already_linked) {
					setLookupStatus('already_linked')
					return
				}

				setLookupStatus('found')
			} catch (error) {
				if (requestId !== requestIdRef.current) return
				const message =
					error?.response?.data?.error ||
					error?.message ||
					'Failed to search for student.'
				if (String(message).toLowerCase().includes('counsellor')) {
					setLookupStatus('counsellor_email')
				} else {
					setLookupStatus('error')
				}
				setLookupResult(null)
				onLookupError?.(error)
			}
		}, DEBOUNCE_MS)

		return () => window.clearTimeout(timer)
	}, [emailQuery, enabled, onLookupError, selectedStudent])

	const selectStudent = useCallback(student => {
		if (!student || student.already_linked) return
		setSelectedStudent(student)
		setEmailQuery(student.email || '')
		setLookupStatus('selected')
	}, [])

	const clearSelection = useCallback(() => {
		setSelectedStudent(null)
		setLookupResult(null)
		setLookupStatus('idle')
		setEmailQuery('')
	}, [])

	return {
		nameQuery,
		setNameQuery,
		emailQuery,
		setEmailQuery,
		lookupResult,
		selectedStudent,
		selectStudent,
		clearSelection,
		lookupStatus,
		reset,
		canSubmit: Boolean(selectedStudent?.id) && lookupStatus === 'selected',
	}
}
